/**
 * Live queue state: fetch once, then refetch whenever the API pushes
 * `queue.updated` over the WebSocket.
 *
 * The push carries no payload by design — it's a "something changed" nudge, so
 * the client re-reads the authoritative queue rather than trying to apply a
 * delta and drift out of sync.
 */
import { useCallback, useEffect, useState } from "react";
import { api, ApiError, type QueueResponse } from "../lib/api";
import { useChannelSocket } from "./useChannelSocket";

export function useQueue(channelId: string) {
  const [data, setData] = useState<QueueResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!channelId) {
      setLoading(false); // no channel selected → don't spin forever
      return;
    }
    try {
      setData(await api.queue(channelId));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [channelId]);

  useEffect(() => {
    // Channel changed — drop the previous channel's data so the skeleton
    // shows instead of leaving its queue on screen while the new one loads.
    setData(null);
    setLoading(true);
    void refresh();
  }, [refresh]);

  const onMessage = useCallback(
    (msg: unknown) => {
      const type = (msg as { type?: string } | null)?.type;
      if (type === "queue.updated") void refresh();
    },
    [refresh],
  );
  const { live } = useChannelSocket(channelId, onMessage);

  return { data, error, loading, live, refresh };
}
